import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import Image from "next/image";
import frameIcon from "../../assets/img/bulkOrder-frame.png";
import discountIcon from "../../assets/icons/additional-discount.png";

interface BuildCartStateProps {
  type: string;
  state: "done" | "processing";
  level1: string;
  level2: string;
}

const BuildCartState: React.FC<BuildCartStateProps> = ({
  type,
  state,
  level1,
  level2,
}) => {
  const textRef = useRef(null);
  const iconRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    if (state === "done") {
      const tl = gsap.timeline();

      // Frame Elastic Bounce
      tl.fromTo(
        frameRef.current,
        { scale: 0.5, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.6, ease: "elastic.out(1, 0.5)" }
      );

      // Icon Pops in First
      tl.fromTo(
        iconRef.current,
        { rotate: -15, opacity: 0, scale: 0.5 },
        {
          rotate: 0,
          opacity: 1,
          scale: 1,
          duration: 0.6,
          ease: "elastic.out(1, 0.6)",
        }
      );

      // Text Appears After Icon
      tl.fromTo(
        textRef.current,
        { scale: 0.5, opacity: 0, y: -10 },
        {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "elastic.out(1, 0.6)",
          stagger: 0.1,
        },
        "-=0.4" // Start slightly before icon finishes
      );
    }
  }, [state]);

  return (
    <div className="w-fit absolute top-0 left-0 flex flex-col items-start">
      {/* Animated Frame */}
      <Image
        ref={frameRef}
        quality={50}
        unoptimized
        src={frameIcon}
        alt="Frame"
        width={34}
        height={34}
        className="absolute -top-[2.5px] -left-[13.5px]"
      />

      {/* Animated Text & Icon */}
      <div ref={textRef} className="mt-[17px] ml-[15px] flex items-center">
        <span
          className={`text-xs font-bold ml-2 whitespace-nowrap flex items-center ${
            state === "done" ? "text-green" : "text-black-1"
          }`}
        >
          {type === "MOQ" ? (
            state === "done" ? (
              <ShoppingBagIcon
                ref={iconRef}
                style={{ fontSize: "14px" }}
                className="text-green"
              />
            ) : (
              <ShoppingBagOutlinedIcon
                ref={iconRef}
                style={{ fontSize: "14px" }}
                className="text-blue"
              />
            )
          ) : (
            <Image
              ref={iconRef}
              src={discountIcon}
              alt="Discount"
              width={15}
              height={15}
            />
          )}
          &nbsp;
          {level1
            ?.split(/(₹\s*\d+(?:\.\d+)?)/g)
            .map((part, index) =>
              part.includes("₹") ? (
                <AnimatedRupee key={index} value={part} />
              ) : (
                <span key={index}>{part}</span>
              )
            )}
        </span>
      </div>

      <p className="text-xs text-gray-500 ml-[26px] mt-[2px] whitespace-nowrap">
        {level2?.split(/(₹\s*\d+(?:\.\d+)?)/g).map((part, index) =>
          part.includes("₹") ? (
            <span key={index} className="font-bold text-black-1 text-sm">
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </p>
    </div>
  );
};

export default BuildCartState;

export const AnimatedRupee: React.FC<{ value: string }> = ({ value }) => {
  const rupeeRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (rupeeRef.current) {
      gsap.killTweensOf(rupeeRef.current); // Kill previous animation
      gsap.fromTo(
        rupeeRef.current,
        { scale: 0.8, opacity: 0.5, y: -5 },
        {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "elastic.out(1, 0.5)",
        }
      );
    }
  }, [value]);

  return (
    <span ref={rupeeRef} className="font-bold text-sm mx-[2px]">
      {value}
    </span>
  );
};
